import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { History } from 'history'
import classNames from 'classnames'

import { Label, PassiveInput } from './input'
import Button from './button'
import GeneratedCodeResults from './campaign-code-generator-results'
import {
  getCampaignCodeGeneratorAsync,
  // getUsersCampaignCodesAsync,
  getCodesByUserGQL,
} from '../store/actions'
import { CampaignCodesState } from '../store/campaign-codes-reducer'
import { GraphQLState } from '../store/graphql-reducer'
import TwoColumns, { Column, Box } from './two-columns'
import styles from '../component-styles/campaign-code-generator.module.scss'
import RequestFieldModal from './request-field-modal'
import { sortData, getValidationChecksObject, copyString } from '../helpers'
import { getLongLink } from '../helpers/campaign-codes'
import useMobile from '../hooks/useMobile'
import ButtonTabs from './button-tabs'
import CampaignCodeGeneratorForm from './campaign-code-generator-form'
import { CampaignCodesCompressed } from '../api-client/api-types'
import { getLocalItem } from '../local-client/local-client'
import CampaignCodeGeneratorCloneTab from './campaign-code-generator-clone-tab'
import BulkImport from './bulk-import'
import Loader from './loader'
import CreateQrModal from './create-qr-modal'
import { FormRow, LabelSlot, FieldSlot } from './row'
import ToolTip from './tooltip'

interface ShowLatestCodeProps {
  latestCodes: any
  generatedStructure: any
}

function ShowLatestCode({
  latestCodes,
  generatedStructure,
}: ShowLatestCodeProps): React.ReactElement | null {
  const [showQrModal, setShowQrModal] = useState(false)

  const { separator, prefix } = useMemo(() => {
    if (generatedStructure && generatedStructure.paramSeparator) {
      const { paramSeparator, masterPrefix } = generatedStructure
      return { separator: paramSeparator, prefix: masterPrefix }
    }
    return { separator: '&', prefix: '?' }
  }, [generatedStructure])

  if (
    !latestCodes ||
    (latestCodes && !Object.prototype.hasOwnProperty.call(latestCodes, 'cL'))
  ) {
    return <CreateQrModal returnQrCodeOnly useCode="" />
  }

  const latestCl = latestCodes.cL[0]
  const useShortLink =
    Object.prototype.hasOwnProperty.call(latestCl, 'shortLinkID') &&
    latestCl.shortLinkID
      ? latestCl.shortLinkID
      : ''

  const singleCode =
    useShortLink !== ''
      ? `https://upl.inc/${useShortLink}`
      : getLongLink(latestCodes.lP, latestCl.tC, separator, prefix)

  return (
    <Box className={styles.noPaddingBox}>
      <FormRow className={classNames(styles.noPadding)}>
        <LabelSlot className={styles.labelSlot}>
          <Label id="link" className={styles.bold}>
            Your last created link
          </Label>
        </LabelSlot>
        <FieldSlot>
          <div className={styles.inlineRow}>
            <PassiveInput id="generated-code" value={singleCode} />
            <Button
              type="copy"
              align="left"
              onClick={(): void => copyString(singleCode)}
            >
              Copy
            </Button>
          </div>
        </FieldSlot>
      </FormRow>
      <FormRow className={classNames(styles.noPadding)}>
        <LabelSlot className={styles.labelSlot}>
          <Label id="qr">QR code</Label>
          <ToolTip
            className={styles.tooltip}
            showOnRight
            message="An optional QR code which can be scanned by a mobile phone camera to access the landing page with campaign codes."
          />
        </LabelSlot>
        <FieldSlot>
          <div className={styles.qrCodeWrapper}>
            <CreateQrModal
              disableCopy
              returnQrCodeOnly
              useCode={singleCode}
              key={singleCode}
            >
              <Button
                className={styles.customiseButton}
                type="edit"
                align="left"
                onClick={(): void => setShowQrModal(true)}
              >
                Customise
              </Button>
              <Button
                className={styles.downloadButton}
                type="download"
                align="left"
                onClick={(): void => setShowQrModal(true)}
              >
                Download
              </Button>
            </CreateQrModal>
          </div>
        </FieldSlot>
      </FormRow>
      {showQrModal && (
        <CreateQrModal
          section="create"
          setShowModal={setShowQrModal}
          useCode={singleCode}
        />
      )}
    </Box>
  )
}

function RequestField({ onClick }: { onClick: any }): React.ReactElement {
  return (
    <Box>
      <Label heading>Request additional value(s)</Label>
      <p>
        If you need a new dropdown value, you can send a request to your admin
        here.
      </p>
      <Button
        type="flatPink"
        className={styles.noMargin}
        align="right"
        onClick={() => {
          onClick()
        }}
      >
        Request value
      </Button>
    </Box>
  )
}

interface Props {
  history: History
}

export default function CampaignCodeGenerator({
  history,
}: Props): React.ReactElement | null {
  const mobile = useMobile(769)
  const isMobileMenu = useMobile(1200)
  const dispatch = useDispatch()
  const {
    usersCodes,
    generatedStructure,
    loadingUsersCodesStatus,
  } = useSelector((s: any): CampaignCodesState => s.campaignCodes)

  const { currentAccount, currentUser } = useSelector(
    (s: any): GraphQLState => s.graphql,
  )
  const accountID = currentAccount?.accountID || ''

  const [latestCodes, setLatestCodes] = useState<any>(null)
  const [generatedCodes, setGeneratedCodes] = useState<string | string[]>('')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [form, setForm] = useState(generatedStructure)
  const [mounted, setMounted] = useState(false)
  const [render, setRender] = useState(false)
  const [fixed, setFixed] = useState(false)
  const [fixedWidth, setFixedWidth] = useState(0)
  const [fixedColumnWidth, setFixedColumnWidth] = useState(0)
  const [fixedLeft, setFixedLeft] = useState(0)
  const [requestFieldActive, setRequestFieldActive] = useState(false)
  const [defaultValue, setDefaultValue] = useState('')

  const fixedColumnRef = useRef<HTMLDivElement>(null)
  const mainColumnRef = useRef<HTMLDivElement>(null)
  const buttonTabsRef = useRef<HTMLDivElement>(null)

  const [scrollTop, setScrollTop] = useState(0)
  // const [padding, setPadding] = useState(0)
  // const [maxOffset, setMaxOffset] = useState(1000)

  useEffect(() => {
    if (accountID !== '') {
      const savedData = getLocalItem('track-create') || null
      if (
        savedData &&
        Object.prototype.hasOwnProperty.call(savedData, accountID)
      ) {
        const startIndex =
          savedData[accountID].options.active === 'single' ? 0 : 1
        setCurrentIndex(startIndex)
      }
      setRender(true)
    }
  }, [accountID])

  useEffect(() => {
    const onScroll = (e) => {
      if (
        buttonTabsRef &&
        buttonTabsRef.current &&
        fixedColumnRef &&
        fixedColumnRef.current &&
        mainColumnRef &&
        mainColumnRef.current
      ) {
        const tabsRect = buttonTabsRef.current.getBoundingClientRect()
        const fixedRect = fixedColumnRef.current.getBoundingClientRect()
        const { innerHeight } = window
        if (
          innerHeight > 650 &&
          ((isMobileMenu && tabsRect.top < 70) ||
            (!isMobileMenu && tabsRect.top < 10))
        ) {
          if (!fixed) {
            setFixed(true)
            setFixedWidth(fixedRect.width)
            setFixedLeft(fixedRect.left - 32)
            setFixedColumnWidth(tabsRect.width)
          }
        } else {
          setFixed(false)
        }
        setScrollTop(e.target.documentElement.scrollTop)
      }
    }
    if (!mobile) {
      window.addEventListener('scroll', onScroll)
    }
    return () => window.removeEventListener('scroll', onScroll)
  }, [scrollTop, isMobileMenu, mobile])

  useEffect((): void => {
    if (!mounted) {
      dispatch(getCampaignCodeGeneratorAsync())
      // dispatch(getUsersCampaignCodesAsync({ startDate: 'last-2-days' }))
      dispatch(getCodesByUserGQL())
      setMounted(true)
    }

    // if (!edited && mounted && generatedStructure !== null) {
    if (mounted && generatedStructure !== null) {
      setForm(generatedStructure)
      setMounted(true)
    }

    // eslint-disable-next-line
  }, [generatedStructure, mounted])

  const orderedUsersCodes = useMemo<CampaignCodesCompressed[]>(() => {
    return sortData(usersCodes, 't', false)
  }, [usersCodes])

  if (!form || !render) {
    return <></>
  }

  const single = currentIndex === 0
  const isAdminUser =
    currentUser &&
    (currentUser.currentAccountPermission === 'admin' ||
      currentUser.currentAccountPermission === 'support')
  const tabsLabels = isAdminUser
    ? ['One at a time', 'Multiple', 'Clone and edit', 'Bulk import']
    : ['One at a time', 'Multiple', 'Clone and edit']

  const validation = getValidationChecksObject(generatedStructure)

  if (!mounted) {
    return (
      <TwoColumns>
        <Column main>
          <Loader height="40px" />
          <Loader height="400px" />
        </Column>
        <Column
          side
          fixed
          fwdRef={fixedColumnRef}
          className={styles.rightColumn}
        >
          <Loader height="400px" />
        </Column>
      </TwoColumns>
    )
  }

  return (
    <TwoColumns>
      <Column main fwdRef={mainColumnRef}>
        <div
          style={{
            maxWidth: fixed ? `${fixedColumnWidth}px` : 'auto',
          }}
        >
          <ButtonTabs
            fwdRef={buttonTabsRef}
            selected={currentIndex}
            isTopOfBox
            tabsLabels={tabsLabels}
            type="tabs"
            onChange={(index: number) => setCurrentIndex(index)}
          >
            <CampaignCodeGeneratorForm
              single
              onGenerateCode={(codes: any[], fullCodes: any) => {
                setGeneratedCodes(codes[0].code)
                setLatestCodes(fullCodes)
              }}
              onRequestNewField={(requestedValue) => {
                setDefaultValue(requestedValue)
                setRequestFieldActive(true)
              }}
            />
            <CampaignCodeGeneratorForm
              single={false}
              onGenerateCode={(resultCodes, fullCodes: any) => {
                setGeneratedCodes(resultCodes)
                setLatestCodes(fullCodes)
              }}
            />
            <CampaignCodeGeneratorCloneTab history={history} />
            <BulkImport
              onSuccess={() => {
                history.push('/track/view-campaign-codes')
              }}
            />
          </ButtonTabs>
        </div>

        {single && (
          <div
            className={classNames({
              [styles.offScreeen]: !latestCodes,
            })}
            style={{
              maxWidth: fixed ? `${fixedColumnWidth}px` : 'auto',
            }}
          >
            <ShowLatestCode
              latestCodes={latestCodes}
              generatedStructure={generatedStructure}
            />
          </div>
        )}

        <div
          style={{
            maxWidth: fixed ? `${fixedColumnWidth}px` : 'auto',
          }}
        >
          {!mobile && (
            <RequestField
              onClick={() => {
                setDefaultValue('')
                setRequestFieldActive(true)
              }}
            />
          )}
        </div>
      </Column>
      <Column
        side
        fixed
        fwdRef={fixedColumnRef}
        className={styles.rightColumn}
        style={{
          position: fixed ? 'fixed' : 'static',
          top: `${isMobileMenu ? 70 : 10}px`,
          left: fixed ? `${fixedLeft}px` : 'auto',
          width: fixed ? `${fixedWidth}px` : `${mobile ? 'auto' : '35%'}`,
        }}
        // style={{ paddingTop: `${mobile ? 0 : padding}px` }}
      >
        <GeneratedCodeResults
          loading={loadingUsersCodesStatus}
          generatedStructure={generatedStructure}
          single={single}
          generatedCodes={generatedCodes}
          codes={orderedUsersCodes}
        />
      </Column>
      {mobile && (
        <RequestField
          onClick={() => {
            setDefaultValue('')
            setRequestFieldActive(true)
          }}
        />
      )}
      {requestFieldActive && (
        <RequestFieldModal
          validation={validation}
          paramDefs={form !== null ? form.paramDefs : []}
          active={requestFieldActive}
          toggleActive={(): void => setRequestFieldActive(!requestFieldActive)}
          defaultValue={defaultValue}
        />
      )}
    </TwoColumns>
  )
}
